'use client'
import AssessmentIcon from '@mui/icons-material/Assessment'
import ContactsIcon from '@mui/icons-material/Contacts'
import HandshakeIcon from '@mui/icons-material/Handshake'
import PsychologyIcon from '@mui/icons-material/Psychology'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import StorageIcon from '@mui/icons-material/Storage'
import {
  Box,
  Button,
  Container,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import LoginModal from '../components/dashboard/LoginModal'
import useAuth from '../hooks/useAuth'
import { parseHostnameFromEnv } from '../lib/brandFromHost'
import { isWysehubAdminHost, isWysehubPortalHost } from '../lib/hostRouting'
import { sanitizeInternalRedirect } from '../utils/safeRedirect'

function canAccessAiClaimsPortal(
  currentUser?: { brandAccess?: string | null } | null,
): boolean {
  return (
    currentUser?.brandAccess === 'aiclaims' ||
    currentUser?.brandAccess === 'both'
  )
}

function PortalEntryPage({
  logoSrc,
  logoAlt,
  overline,
  title,
  description,
  ctaLabel,
  user,
  onSignIn,
}: {
  logoSrc: string
  logoAlt: string
  overline: string
  title: string
  description: string
  ctaLabel: string
  user: unknown
  onSignIn: () => void
}) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'linear-gradient(180deg, #f8fafc 0%, #e2f4f7 45%, #ffffff 100%)',
        color: '#0f172a',
      }}
    >
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 14 } }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Box
            component="img"
            src={logoSrc}
            alt={logoAlt}
            sx={{ width: 220, maxWidth: '70vw' }}
          />
          <Stack spacing={2} sx={{ maxWidth: 720 }}>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: '0.18em',
                color: '#0891b2',
                fontWeight: 700,
              }}
            >
              {overline}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.04em',
                fontSize: { xs: '2.5rem', md: '4.25rem' },
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              {description}
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button variant="contained" size="large" onClick={onSignIn}>
              {user ? ctaLabel : `Sign in to ${ctaLabel}`}
            </Button>
            <Button
              variant="outlined"
              size="large"
              component="a"
              href="/privacy"
            >
              Privacy
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

function HomePage() {
  const theme = useTheme()
  const router = useRouter()
  const [isMounted, setIsMounted] = useState(false)
  const [openLoginModal, setOpenLoginModal] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const { user } = useAuth()
  const portalHostname = parseHostnameFromEnv(
    process.env.NEXT_PUBLIC_AICLAIMS_PORTAL_HOST,
  )
  const isAiClaimsPortalHost =
    isMounted && !!portalHostname && window.location.hostname === portalHostname
  const isWysehubPortalSubdomain =
    isMounted && isWysehubPortalHost(window.location.hostname)
  const isWysehubAdminSubdomain =
    isMounted && isWysehubAdminHost(window.location.hostname)
  const words = 'The AI Driven Solution For Your Outsourced Needs'.split(' ')
  const [scrollY, setScrollY] = useState<number>(-0)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY)
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Check for login parameter in URL and open login modal if present
  useEffect(() => {
    const currentSearchParams = new URLSearchParams(window.location.search)
    const shouldOpenLogin = currentSearchParams.get('login') === 'true'

    if (isAiClaimsPortalHost && user && canAccessAiClaimsPortal(user)) {
      router.replace('/aiclaims/dashboard')
      return
    }

    if (isWysehubPortalSubdomain && user) {
      router.replace('/homehub')
      return
    }

    if (isWysehubAdminSubdomain && user) {
      router.replace('/admin')
      return
    }

    // Only show login modal if user is not already authenticated
    if (shouldOpenLogin && !user) {
      setOpenLoginModal(true)

      // Store the 'next' parameter for redirection after login
      const nextUrl = sanitizeInternalRedirect(
        currentSearchParams.get('next'),
        window.location.origin,
      )
      if (nextUrl) {
        localStorage.setItem('loginRedirectUrl', nextUrl)
      } else {
        localStorage.removeItem('loginRedirectUrl')
      }
    } else if (shouldOpenLogin && user) {
      // If user is already authenticated and login=true is in URL,
      // check if there's a redirect URL and navigate there directly
      const nextUrl = sanitizeInternalRedirect(
        currentSearchParams.get('next'),
        window.location.origin,
      )
      if (nextUrl) {
        router.push(nextUrl)
      }
    }
  }, [
    isAiClaimsPortalHost,
    isWysehubAdminSubdomain,
    isWysehubPortalSubdomain,
    user,
    router,
  ])

  const getColor = (index: number) => {
    const offset = 10
    const scrollSpeed = 15
    const wordIndex = Math.floor((scrollY - offset) / scrollSpeed)
    return index <= wordIndex
      ? theme.palette.success.main
      : theme.palette.text.secondary
  }

  const handleHomeHubClick = () => {
    if (isAiClaimsPortalHost) {
      if (user) {
        if (canAccessAiClaimsPortal(user)) {
          router.push('/aiclaims/dashboard')
        } else {
          router.replace('/')
        }
      } else {
        setOpenLoginModal(true)
      }
      return
    }

    if (isWysehubAdminSubdomain) {
      if (user) {
        router.push('/admin')
      } else {
        setOpenLoginModal(true)
      }
      return
    }

    if (isWysehubPortalSubdomain) {
      if (user) {
        router.push('/homehub')
      } else {
        setOpenLoginModal(true)
      }
      return
    }

    if (user) {
      // User is authenticated, navigate to home hub
      router.push('/homehub')
    } else {
      // User is not authenticated, show login modal
      setOpenLoginModal(true)
    }
  }

  const handleCloseLoginModal = () => setOpenLoginModal(false)

  if (!isMounted) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 14 } }}>
        <Stack spacing={3} alignItems="center">
          <Skeleton variant="rectangular" width={220} height={64} />
          <Skeleton variant="text" width="60%" height={56} />
          <Skeleton variant="text" width="80%" height={32} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Skeleton variant="rounded" width={180} height={44} />
            <Skeleton variant="rounded" width={120} height={44} />
          </Stack>
        </Stack>
      </Container>
    )
  }

  if (isAiClaimsPortalHost) {
    return (
      <>
        <PortalEntryPage
          logoSrc="/AiClaimsBlackLogo.svg"
          logoAlt="AI Claims logo"
          overline="Portal Access"
          title="Access the AI Claims operations portal."
          description="Sign in to review dashboards, recent runs, workflows, cases, and operational cost views for the AI Claims environment."
          ctaLabel="Open Portal"
          user={user}
          onSignIn={handleHomeHubClick}
        />
        <LoginModal open={openLoginModal} onClose={handleCloseLoginModal} />
      </>
    )
  }

  if (isWysehubPortalSubdomain || isWysehubAdminSubdomain) {
    return (
      <>
        <PortalEntryPage
          logoSrc="/assets/images/WH-logo.png"
          logoAlt="WyseHub logo"
          overline={isWysehubAdminSubdomain ? 'Admin Access' : 'Portal Access'}
          title={
            isWysehubAdminSubdomain
              ? 'Access the WyseHub administration area.'
              : 'Access the WyseHub partner portal.'
          }
          description={
            isWysehubAdminSubdomain
              ? 'Sign in with a system administration account to manage users, partners, settings, workflows, and operational controls.'
              : 'Sign in to review referrals, claims, dashboards, requests, and daily operational activity across the WyseHub platform.'
          }
          ctaLabel={isWysehubAdminSubdomain ? 'Open Admin' : 'Open Portal'}
          user={user}
          onSignIn={handleHomeHubClick}
        />
        <LoginModal open={openLoginModal} onClose={handleCloseLoginModal} />
      </>
    )
  }

  return (
    <Box
      sx={{
        backgroundImage: "url('/assets/images/Visiwig-Subtle-Waves.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        color: 'white',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
            width: '100%',
          }}
        >
          <Box>
            <Box
              component="img"
              src="/assets/images/WH-logo.png"
              alt="WyseHub logo"
              sx={{ maxWidth: 200, mb: 0 }}
              loading="lazy"
            />
          </Box>
          <Box>
            <Button
              onClick={handleHomeHubClick}
              variant="outlined"
              size="large"
              sx={{
                borderColor: theme.palette.success.main,
                color: theme.palette.success.main,
                '&:hover': {
                  backgroundColor: theme.palette.success.main,
                  color: 'white',
                },
              }}
            >
              {user ? 'Go to HomeHub' : 'Go to HomeHub'}
            </Button>
          </Box>
        </Box>
        <Stack spacing={2} textAlign="center" mt={4} mb={6}>
          <Typography
            variant="h1"
            fontWeight="bold"
            fontStyle="italic"
            lineHeight={1.2}
            sx={{ fontSize: { xs: '6vw', md: '3.5vw' } }}
          >
            {words.map((word: string, index: number) => (
              <Box component="span" key={index} sx={{ color: getColor(index) }}>
                {word}{' '}
              </Box>
            ))}
          </Typography>
        </Stack>
      </Container>

      <Container maxWidth="md" sx={{ py: 2 }}>
        {/* Top 3 cards in a row */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 2,
          }}
        >
          {[
            {
              icon: <PsychologyIcon />,
              title: 'Intelligent Partner Matching',
              description:
                'Our platform connects each claim with the best-suited outsourced partner for optimal outcomes and customer satisfaction.',
            },
            {
              icon: <HandshakeIcon />,
              title: 'Seamless Integration',
              description:
                'Effortlessly integrate with our system to create a unified, efficient, and transparent claims processing experience.',
            },
            {
              icon: <StorageIcon />,
              title: 'Data Driven Decisions',
              description:
                'Harness the power of data to enhance accuracy and speed in claims handling, reducing turnaround times and increasing conversion.',
            },
          ].map((item, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden',
              }}
            >
              {/* Top section with icon and title */}
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  py: 3,
                  px: 2,
                  textAlign: 'center',
                  height: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    fontSize: 54,
                    color: theme.palette.success.main,
                    mb: 1,
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color={theme.palette.success.main}
                  textAlign="center"
                  sx={{ fontSize: '1.28rem', lineHeight: 1.3 }}
                >
                  {item.title}
                </Typography>
              </Box>

              {/* Bottom section with description */}
              <Box
                sx={{
                  backgroundColor: theme.palette.success.main,
                  py: 3,
                  px: 2,
                  textAlign: 'center',
                  height: 210,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: '1.2rem', lineHeight: 1.4 }}
                >
                  {item.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Bottom 2 cards in a row */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 2,
          }}
        >
          {[
            {
              icon: <AssessmentIcon />,
              title: 'On Demand Stats',
              description:
                'Access our web-based reporting software for KPIs, unique AI-based claim scores, and Wysehub comparisons. Generate, edit, and export reports all from our platform.',
            },
            {
              icon: <RocketLaunchIcon />,
              title: 'Scalable Solutions',
              description:
                'Scale your operations seamlessly with our availability and range of outsourced partners instantly tailored to your needs.',
            },
          ].map((item, i) => (
            <Box key={i} sx={{ flex: 1 }}>
              <Box
                sx={{
                  backgroundColor: theme.palette.success.main,
                  py: 4,
                  px: 3,
                  textAlign: 'center',
                  height: 360,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
              >
                <Box>
                  <Box sx={{ fontSize: 54, color: 'black', mb: 2 }}>
                    {item.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="black"
                    mb={2}
                    sx={{ fontSize: '1.5rem' }}
                  >
                    {item.title}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '1.2rem',
                    lineHeight: 1.4,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            width: '100%',
            py: 4,
            textAlign: 'center',
            mb: 2,
            position: 'relative',
            backgroundColor: isPlaying
              ? 'transparent'
              : theme.palette.customColors.darkSlate,
            minHeight: 250,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          {!isPlaying && (
            <Skeleton
              variant="rectangular"
              sx={{
                margin: '0 auto',
                width: '85%',
                height: 250,
                borderRadius: '12px',
              }}
            />
          )}
          {!isPlaying && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  padding: '8px 16px',
                  fontSize: '21px',
                  backgroundColor: theme.palette.success.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
                onClick={() => setIsPlaying(true)}
              >
                Watch Our Video
              </Button>
            </Box>
          )}
          {isPlaying && (
            <Box
              sx={{
                position: 'relative',
                paddingTop: '47.8125%', // 16:9 aspect ratio: 56.25% × 85% width
                width: '85%',
                margin: '0 auto',
              }}
            >
              <Box
                data-testid="video-player"
                component="video"
                src="https://whvideo.ams3.cdn.digitaloceanspaces.com/-MAIN%20COMP_5.mp4"
                autoPlay
                muted
                controls
                loop
                playsInline
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            backgroundColor: theme.palette.customColors.darkSlate,
            py: 3,
            px: 3,
            mb: 2,
            textAlign: 'left',
            width: '100%',
            color: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box
              component="img"
              src="/assets/images/AA_Logo.png"
              alt="AnnieAi logo"
              sx={{ maxWidth: 120 }}
              loading="lazy"
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ color: 'white', lineHeight: 1.4, fontSize: '1.2rem' }}
          >
            We have teamed up with AnnieAi to provide the latest machine
            learning technology for greater accuracy in claims scoring.
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: theme.palette.success.main,
            py: 3,
            px: 3,
            textAlign: 'left',
            width: '100%',
            color: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ fontSize: 36, color: 'white', mr: 1 }}>
              <ContactsIcon />
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ fontSize: '1.35rem' }}
            >
              Let's start a conversation
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
            Get in contact with us to discuss how Wysehub can help you.
          </Typography>
        </Box>
      </Container>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 4,
          mb: 4,
        }}
      >
        <Box
          component="img"
          src="/assets/images/WH-logo.png"
          alt="WyseHub logo"
          sx={{ maxWidth: 150 }}
        />
        <Typography variant="h5" mx={2} fontWeight="bold" color="black">
          x
        </Typography>
        <Box
          component="img"
          src="/assets/images/AA_Logo.png"
          alt="AnnieAi logo"
          sx={{ maxWidth: 150 }}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: theme.palette.customColors.darkSlate,
          py: 3,
          mt: 2,
        }}
      >
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography variant="body2">
            © 2024 WyseHub. All rights reserved.
          </Typography>
        </Container>
      </Box>

      <LoginModal open={openLoginModal} onClose={handleCloseLoginModal} />
    </Box>
  )
}

export default HomePage
